// Shared (inline-styled) renderer for the keyboard-shortcut registry, grouped by
// category. Used by both the `?` modal and the /shortcuts page so they never drift.
import { InstrumentTypeResponse } from "../../codegen-api";
import { isInstrumentTypeEnabled } from "../../config/features";
import type { CSSProperties } from "react";
import { SHORTCUTS, formatChord } from "keyboard/shortcuts";
import type { ShortcutCategory } from "keyboard/types";
import {
  BORDER_COLORS,
  FIELD_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";

const CATEGORY_ORDER: ShortcutCategory[] = ["Global", "Go to", "Trade", "Navigation"];

export function ShortcutsList() {
  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    // Also drop shortcuts for a vertical this build does not offer —
    // "Adjust leverage" is meaningless on a spot-only deployment.
    items: SHORTCUTS.filter(
      (s) =>
        s.category === category &&
        !s.hidden &&
        (!s.requiresInstrument ||
          isInstrumentTypeEnabled(
            s.requiresInstrument as InstrumentTypeResponse
          ))
    ),
  })).filter((g) => g.items.length > 0);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px 32px" }}>
      {groups.map((group) => (
        <div key={group.category}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: TEXT_COLORS.three, marginBottom: 8 }}>
            {group.category}
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {group.items.map((s) => (
              <li key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 13, color: TEXT_COLORS.two }}>{s.label}</span>
                <span style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  {(s.displayKeys
                    ? [s.displayKeys]
                    : formatChord(s.keys).split(" ")
                  ).map((p, i) => (
                    <Kbd key={i}>{p}</Kbd>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd style={kbdStyle}>{children}</kbd>;
}

export const kbdStyle: CSSProperties = {
  display: "inline-flex",
  minWidth: 22,
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${BORDER_COLORS.three}`,
  background: FIELD_COLORS.one,
  borderRadius: 4,
  padding: "2px 6px",
  fontFamily: "monospace",
  fontSize: 12,
  color: TEXT_COLORS.one,
};
